// Local match-scoring port from /tmp/erbi-design/erbi/project/recon-data.jsx
// lines 108-145 (matchScore) and 148-177 (matchSignals).
//
// Pure functions. Used to highlight likely matches on the *other* side as
// the user makes a selection in the workspace. The server returns the same
// shape from POST /reconciliation/suggest/ (per SPEC §6.2) — this local copy
// keeps the UI responsive on selection without round-tripping for every
// click.
//
// Input shapes match the API row shapes from SPEC §6.1:
//   sapRow:    { amount, inferred_vendor, reference_field, posting_date, ... }
//   invoice:   { amount, vendor, id, invoice_number, project, sent_to_ap_at, ... }
// Both inputs also support the design-doc shape ({ docNo, inferredVendor,
// reference, postingDate } / { sentToAp, vendor }) so workspace highlights
// work whether the projection has been normalized or not.

function getAmount(x) {
  if (!x) return 0;
  // Prefer the locked exact-date USD value (issue #40) so cross-currency pairs
  // (EUR row vs USD invoice) compare on the same currency. Falls back to the
  // native amount when no USD value is locked (USD rows, or FX still pending).
  return Number(x.amount_usd ?? x.amount ?? 0);
}
function getInferredVendor(sap) {
  return sap.inferred_vendor ?? sap.inferredVendor ?? '';
}
function getReference(sap) {
  return sap.reference_field ?? sap.reference ?? '';
}
function getPostingDate(sap) {
  return sap.posting_date ?? sap.postingDate ?? null;
}
function getInvoiceVendor(inv) {
  return inv.vendor ?? '';
}
function getInvoiceIdent(inv) {
  // The reference token check looks at the invoice's id + project text.
  // Use both invoice_number (API) and id (design) so either works.
  return `${inv.invoice_number ?? inv.id ?? ''} ${inv.project ?? ''}`;
}
function getSentToAp(inv) {
  return inv.sent_to_ap_at ?? inv.sentToAp ?? null;
}
function getInvoiceDate(inv) {
  return inv.invoice_date ?? inv.invoiceDate ?? null;
}

// Whole-day distance between two dates, or null if either is missing/invalid.
function proximityDays(a, b) {
  if (!a || !b) return null;
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  if (Number.isNaN(da) || Number.isNaN(db)) return null;
  return Math.abs(da - db) / 86400000;
}

// Shared date tiers: ≤7d = strong (0.10), ≤21d = soft (0.05), else no signal.
function dateTier(days) {
  if (days == null) return null;
  if (days <= 7) return { score: 0.1, tone: 'good' };
  if (days <= 21) return { score: 0.05, tone: 'soft' };
  return null;
}

// Date proximity, preferring sent-to-AP. We compare the SAP posting date to
// the sent-to-AP date first; if THAT lands in a tier we use it and stop. Only
// when sent-to-AP is missing or too far do we fall through to the invoice
// date. (Per product decision: prefer AP, then invoice — not whichever is
// closer.) Returns { tier, days, basis } or null.
function dateProximity(sapRow, invoice) {
  const sapD = getPostingDate(sapRow);
  if (!sapD) return null;
  const apDays = proximityDays(sapD, getSentToAp(invoice));
  const apTier = dateTier(apDays);
  if (apTier) return { tier: apTier, days: apDays, basis: 'ap' };
  const invDays = proximityDays(sapD, getInvoiceDate(invoice));
  const invTier = dateTier(invDays);
  if (invTier) return { tier: invTier, days: invDays, basis: 'invoice' };
  return null;
}

export function matchScore(sapRow, invoice) {
  if (!sapRow || !invoice) return 0;

  // Amount component (max 0.40).
  const a1 = Math.abs(getAmount(sapRow));
  const a2 = Math.abs(getAmount(invoice));
  const max = Math.max(a1, a2);
  const delta = max > 0 ? Math.abs(a1 - a2) / max : 1;
  const amtScore =
    delta < 0.001 ? 0.40 :
    delta < 0.01  ? 0.36 :
    delta < 0.03  ? 0.24 :
    delta < 0.08  ? 0.10 : 0;

  // Vendor component (max 0.30).
  let venScore = 0;
  const inferred = getInferredVendor(sapRow);
  if (inferred && inferred !== '—') {
    const a = inferred.toLowerCase();
    const b = getInvoiceVendor(invoice).toLowerCase();
    if (a === b) venScore = 0.30;
    else if (a.split(/[\s,.]+/).some(t => t.length > 3 && b.includes(t))) venScore = 0.18;
  }

  // Reference token component (max 0.20).
  let refScore = 0;
  const ref = (getReference(sapRow) || '').toLowerCase();
  if (ref && !ref.startsWith('no ') && !ref.startsWith('unmapped')) {
    const tokens = ref.split(/[\s\-_/]+/).filter(t => t.length >= 3);
    const target = getInvoiceIdent(invoice).toLowerCase();
    const hits = tokens.filter(t => target.includes(t)).length;
    if (hits >= 2) refScore = 0.20;
    else if (hits === 1) refScore = 0.10;
  }

  // Date proximity component (max 0.10) — prefer sent-to-AP, fall back to
  // invoice date. See dateProximity().
  const dateMatch = dateProximity(sapRow, invoice);
  const dateScore = dateMatch ? dateMatch.tier.score : 0;

  return Math.min(1, amtScore + venScore + refScore + dateScore);
}

export function matchSignals(sapRow, invoice) {
  const out = [];
  if (!sapRow || !invoice) return out;

  const a1 = Math.abs(getAmount(sapRow));
  const a2 = Math.abs(getAmount(invoice));
  const max = Math.max(a1, a2);
  const delta = max > 0 ? Math.abs(a1 - a2) / max : 1;
  if (delta < 0.001)
    out.push({
      kind: 'amount',
      tone: 'good',
      label: 'Amount equal',
      tip: 'The SAP amount and the invoice amount are identical.',
    });
  else if (delta < 0.03)
    out.push({
      kind: 'amount',
      tone: 'soft',
      label: `Amount Δ ${(delta * 100).toFixed(1)}%`,
      tip: `The amounts differ by ${(delta * 100).toFixed(1)}% (SAP vs invoice).`,
    });

  const inferred = getInferredVendor(sapRow);
  if (inferred && inferred !== '—') {
    const a = inferred.toLowerCase();
    const b = getInvoiceVendor(invoice).toLowerCase();
    if (a === b)
      out.push({
        kind: 'vendor',
        tone: 'good',
        label: 'Vendor matches',
        tip: "The vendor SAP inferred for this row matches the invoice's vendor.",
      });
    else if (a.split(/[\s,.]+/).some(t => t.length > 3 && b.includes(t)))
      out.push({
        kind: 'vendor',
        tone: 'soft',
        label: 'Vendor partial',
        tip: "Part of the SAP vendor name appears in the invoice's vendor.",
      });
  }

  const ref = (getReference(sapRow) || '').toLowerCase();
  if (ref && !ref.startsWith('no ') && !ref.startsWith('unmapped')) {
    const tokens = ref.split(/[\s\-_/]+/).filter(t => t.length >= 3);
    const target = getInvoiceIdent(invoice).toLowerCase();
    const matched = tokens.filter(t => target.includes(t));
    if (matched.length >= 1) {
      const words = matched.map(t => `“${t}”`).join(', ');
      out.push({
        kind: 'reference',
        tone: matched.length >= 2 ? 'good' : 'soft',
        // "Reference matches" when 2+ words line up, "Reference partly
        // matches" for a single word — clearer than the old "ref token".
        label: matched.length >= 2 ? 'Reference matches' : 'Reference partly matches',
        tip:
          `${matched.length >= 2 ? 'Words' : 'The word'} ${words} from the SAP ` +
          `reference also ${matched.length >= 2 ? 'appear' : 'appears'} in the ` +
          `invoice number/project.`,
      });
    }
  }

  const dateMatch = dateProximity(sapRow, invoice);
  if (dateMatch) {
    const d = Math.round(dateMatch.days);
    const plural = d === 1 ? '' : 's';
    if (dateMatch.basis === 'ap') {
      out.push({
        kind: 'date',
        tone: dateMatch.tier.tone,
        label: `${d}d after AP`,
        tip: `SAP posted this ${d} day${plural} after the invoice was sent to AP.`,
      });
    } else {
      out.push({
        kind: 'date',
        tone: dateMatch.tier.tone,
        label: d === 0 ? 'Same day as invoice' : `${d}d from invoice`,
        tip:
          d === 0
            ? 'SAP posted on the same day as the invoice date.'
            : `SAP posted ${d} day${plural} from the invoice date.`,
      });
    }
  }

  return out;
}

// Plain-English summary of WHAT lines up between a SAP row and an invoice —
// used as the hover tooltip on the % suggestion chip. Deliberately lists the
// matching factors rather than the point math, so "50%" stops being a mystery.
export function matchSummary(sapRow, invoice) {
  const sig = matchSignals(sapRow, invoice);
  if (!sig.length) return 'No strong matching signals — based on amount proximity only.';
  return 'What matches: ' + sig.map(s => s.label).join(' · ');
}

// Rank every unmatched SAP row × invoice pair, keep those at/above `threshold`,
// and greedily resolve to 1-to-1 matches (highest-confidence pairing claims
// each row/invoice first). Used by the "Show probable matches" assisted-
// reconcile list. Returns [{ sap, invoice, score, signals }] sorted high→low.
//
// `amountTolerancePct` (percent; undefined = no gate) drops any pair whose USD
// amount delta `|a1-a2|/max(a1,a2)` exceeds the tolerance BEFORE the score
// threshold — the operator-adjustable manual-review band (issue #40). Amounts
// come from getAmount, i.e. the locked USD value where available.
export function probableMatches(sapRows, invoices, threshold = 0.45, amountTolerancePct) {
  const gate = amountTolerancePct == null ? null : Number(amountTolerancePct) / 100;
  const pairs = [];
  (sapRows || []).forEach((s) => {
    (invoices || []).forEach((inv) => {
      if (gate != null) {
        const a1 = Math.abs(getAmount(s));
        const a2 = Math.abs(getAmount(inv));
        const max = Math.max(a1, a2);
        const delta = max > 0 ? Math.abs(a1 - a2) / max : 1;
        if (delta > gate) return;
      }
      const score = matchScore(s, inv);
      if (score >= threshold) pairs.push({ sap: s, invoice: inv, score });
    });
  });
  pairs.sort((a, b) => b.score - a.score);

  const usedSap = new Set();
  const usedInv = new Set();
  const out = [];
  for (const p of pairs) {
    if (usedSap.has(p.sap.id) || usedInv.has(p.invoice.id)) continue;
    usedSap.add(p.sap.id);
    usedInv.add(p.invoice.id);
    out.push({ ...p, signals: matchSignals(p.sap, p.invoice) });
  }
  return out;
}
